import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const VehicleForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, post, put, loading } = useApi();
  const { user: currentUser } = useAuth();
  
  const [formData, setFormData] = useState({
    brand: '',
    color: '',
    plates: '',
    other: '',
    startedAt: '',
    endedAt: '',
    isActive: true
  });
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) {
      fetchVehicle();
    }
  }, [id]);

  const fetchVehicle = async () => {
    const { data } = await get(`/api/vehicles/${id}`);
    if (data) {
      setFormData({
        brand: data.brand || '',
        color: data.color || '',
        plates: data.plates || '',
        other: data.other || '',
        startedAt: data.startedAt ? data.startedAt.slice(0, 16) : '',
        endedAt: data.endedAt ? data.endedAt.slice(0, 16) : '',
        isActive: data.isActive !== undefined ? data.isActive : true
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.brand.trim()) {
      newErrors.brand = 'Brand is required';
    }
    if (!formData.color.trim()) {
      newErrors.color = 'Color is required';
    }
    if (!formData.plates.trim()) {
      newErrors.plates = 'Plates are required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const submitData = {
      brand: formData.brand,
      color: formData.color,
      plates: formData.plates,
      other: formData.other,
      startedAt: formData.startedAt || null,
      endedAt: formData.endedAt || null,
      isActive: formData.isActive
    };

    let response;
    if (id) {
      response = await put(`/api/vehicles/${id}`, submitData);
    } else {
      response = await post('/api/vehicles', submitData);
    }

    if (!response.error) {
      navigate('/vehicles/filter');
    }
  };

  if (loading && id) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/vehicles/filter')}
          className="mr-4 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
          {id ? 'Edit Vehicle' : 'Add New Vehicle'}
        </h1>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Brand */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Brand <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                className={`input-field ${errors.brand ? 'border-red-500' : ''}`}
                placeholder="Enter brand"
              />
              {errors.brand && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.brand}</p>
              )}
            </div>

            {/* Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Color <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="color"
                value={formData.color}
                onChange={handleChange}
                className={`input-field ${errors.color ? 'border-red-500' : ''}`}
                placeholder="Enter color"
              />
              {errors.color && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.color}</p>
              )}
            </div>

            {/* Plates */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Plates <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="plates"
                value={formData.plates}
                onChange={handleChange}
                className={`input-field ${errors.plates ? 'border-red-500' : ''}`}
                placeholder="Enter plates"
              />
              {errors.plates && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.plates}</p>
              )}
            </div>

            {/* Other */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Other Information
              </label>
              <textarea
                name="other"
                value={formData.other}
                onChange={handleChange}
                rows="3"
                className="input-field"
                placeholder="Enter other information"
              />
            </div>

            {/* Started At */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Started At
              </label>
              <input
                type="datetime-local"
                name="startedAt"
                value={formData.startedAt}
                onChange={handleChange}
                className="input-field"
              />
            </div>

            {/* Ended At */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Ended At
              </label>
              <input
                type="datetime-local"
                name="endedAt"
                value={formData.endedAt}
                onChange={handleChange}
                className="input-field"
              />
            </div>

            {/* Active Status */}
            <div className="md:col-span-2">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  className="h-4 w-4 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Active</span>
              </label>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => navigate('/vehicles/filter')}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : id ? 'Update Vehicle' : 'Create Vehicle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VehicleForm;